import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { createTileLayer } from '../utils/mapTiles';

export default function MapView({
  hubs = [],
  stops = [],
  plannedPolyline = null,
  routeColor = '#38bdf8',
  geohistory = [],
  selectedStopId,
  onSelectStop
}) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef({});
  const plannedPolylineRef = useRef(null);
  const geohistoryPolylineRef = useRef(null);
  const driverMarkerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!leafletMap.current) {
      // Default view centered on Lausanne / Renens region (Switzerland)
      leafletMap.current = L.map(mapRef.current, {
        center: [46.52, 6.63],
        zoom: 12,
        zoomControl: false
      });

      L.control.zoom({ position: 'topright' }).addTo(leafletMap.current);
      createTileLayer('pale').addTo(leafletMap.current);
    }

    const map = leafletMap.current;

    // Clear existing markers & polylines
    Object.values(markersRef.current).forEach(m => map.removeLayer(m));
    markersRef.current = {};

    if (plannedPolylineRef.current) {
      map.removeLayer(plannedPolylineRef.current);
      plannedPolylineRef.current = null;
    }
    if (geohistoryPolylineRef.current) {
      map.removeLayer(geohistoryPolylineRef.current);
      geohistoryPolylineRef.current = null;
    }
    if (driverMarkerRef.current) {
      map.removeLayer(driverMarkerRef.current);
      driverMarkerRef.current = null;
    }

    const allPointsForBounds = [];
    const fallbackRouteLatLngs = [];

    // 1. Render Hub Markers (Simple sleek square)
    hubs.forEach(hub => {
      const lat = parseFloat(hub.latitude);
      const lng = parseFloat(hub.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const hubIcon = L.divIcon({
          className: 'custom-hub-icon',
          html: `<div style="background:#0f172a; border:3px solid #38bdf8; border-radius:4px; width:30px; height:30px; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 12px rgba(0,0,0,0.6);"><div style="background:#38bdf8; width:12px; height:12px; border-radius:2px;"></div></div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15]
        });

        const marker = L.marker([lat, lng], { icon: hubIcon })
          .bindPopup(`<b>${hub.name}</b> (Hub)<br/>${hub.address || ''}, ${hub.city || ''}`)
          .addTo(map);

        markersRef.current[`hub-${hub.id}`] = marker;
        fallbackRouteLatLngs.push([lat, lng]);
        allPointsForBounds.push([lat, lng]);
      }
    });

    // 2. Render Stop Markers
    stops.forEach((stop, idx) => {
      const lat = parseFloat(stop.latitude);
      const lng = parseFloat(stop.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const isDelivered = stop.status === 'delivered';
        const isFailed = stop.status === 'failed';
        const isSelected = selectedStopId === stop.id;

        let bgColor = '#3b82f6'; // Blue pending
        if (isDelivered) bgColor = '#22c55e'; // Green delivered
        if (isFailed) bgColor = '#ef4444'; // Red failed

        const displayNum = stop.real_index !== null && stop.real_index !== undefined
          ? stop.real_index
          : (stop.sequence_order || idx + 1);

        const iconHtml = `<div style="background:${bgColor}; color:#ffffff; border:2px solid ${isSelected ? '#38bdf8' : '#ffffff'}; border-radius:50%; width:28px; height:28px; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:12px; box-shadow:${isSelected ? '0 0 16px #38bdf8' : '0 3px 8px rgba(0,0,0,0.4)'}; transition:all 0.15s ease;">${isDelivered ? '✓' : displayNum}</div>`;

        const stopIcon = L.divIcon({
          className: 'custom-stop-icon',
          html: iconHtml,
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        });

        const marker = L.marker([lat, lng], { icon: stopIcon })
          .bindPopup(`
            <div style="font-family:sans-serif; padding:4px;">
              <strong style="font-size:13px; color:#0f172a;">${stop.client_name || 'Client'}</strong><br/>
              <span style="font-size:11px; color:#64748b;">${stop.address || ''}, ${stop.city || ''}</span><br/>
              <div style="margin-top:6px; font-size:11px; display:flex; justify-content:space-between; gap:8px;">
                <span>Statut: <b style="color:${bgColor};">${stop.status.toUpperCase()}</b></span>
                <span>Poids: <b>${Math.round(stop.weight_kg || 0)} kg</b></span>
              </div>
            </div>
          `)
          .addTo(map);

        marker.on('click', () => {
          if (onSelectStop) onSelectStop(stop.id);
        });

        markersRef.current[`stop-${stop.id}`] = marker;
        fallbackRouteLatLngs.push([lat, lng]);
        allPointsForBounds.push([lat, lng]);
      }
    });

    // Close the fallback route loop back to the return hub if provided
    if (hubs.length > 0 && fallbackRouteLatLngs.length > 1) {
      const endHub = hubs[hubs.length - 1];
      if (endHub && !isNaN(parseFloat(endHub.latitude))) {
        fallbackRouteLatLngs.push([parseFloat(endHub.latitude), parseFloat(endHub.longitude)]);
      }
    }

    // 3. Render Planned Route: DOTTED LINE (théorique)
    const plannedCoords = (Array.isArray(plannedPolyline) && plannedPolyline.length > 1)
      ? plannedPolyline
      : fallbackRouteLatLngs;

    if (plannedCoords && plannedCoords.length > 1) {
      plannedPolylineRef.current = L.polyline(plannedCoords, {
        color: routeColor || '#38bdf8',
        weight: 3,
        opacity: 0.85,
        dashArray: '6, 8'
      }).addTo(map);

      plannedPolylineRef.current.bindTooltip('<b>Itinéraire planifié (théorique)</b>');
    }

    // 4. Render Actual Driver Geohistory: SOLID LINE (tracé réel)
    const geohistoryLatLngs = (geohistory || [])
      .filter(p => !isNaN(parseFloat(p.latitude)) && !isNaN(parseFloat(p.longitude)))
      .map(p => {
        const pt = [parseFloat(p.latitude), parseFloat(p.longitude)];
        allPointsForBounds.push(pt);
        return pt;
      });

    if (geohistoryLatLngs.length > 1) {
      geohistoryPolylineRef.current = L.polyline(geohistoryLatLngs, {
        color: '#f59e0b', // Vibrant amber/orange
        weight: 3.5,
        opacity: 0.95,
        dashArray: null
      }).addTo(map);

      geohistoryPolylineRef.current.bindTooltip('<b>Tracé réel livreur (GPS)</b>');
    }

    // 5. Driver Current / Latest Position Marker (with pulsating beacon)
    if (geohistory && geohistory.length > 0) {
      const latest = geohistory[geohistory.length - 1];
      const lat = parseFloat(latest.latitude);
      const lng = parseFloat(latest.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const pulseHtml = `
          <div style="position:relative; width:32px; height:32px; display:flex; align-items:center; justify-content:center;">
            <div style="position:absolute; width:28px; height:28px; border-radius:50%; background:#f59e0b; opacity:0.35; animation:pulse 2s infinite ease-in-out;"></div>
            <div style="position:relative; width:18px; height:18px; border-radius:50%; background:#f59e0b; border:2.5px solid #ffffff; box-shadow:0 0 10px rgba(245, 158, 11, 0.8); display:flex; align-items:center; justify-content:center; color:#0f172a; font-size:10px; font-weight:900;">
              ${latest.heading !== null && latest.heading !== undefined ? `<span style="transform:rotate(${latest.heading}deg); display:inline-block;">▲</span>` : '●'}
            </div>
          </div>
        `;

        const driverIcon = L.divIcon({
          className: 'driver-pulse-pin',
          html: pulseHtml,
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });

        const timeStr = latest.recorded_at ? new Date(latest.recorded_at).toLocaleTimeString() : '';
        const speedStr = latest.speed !== null && latest.speed !== undefined ? `${Math.round(latest.speed)} km/h` : 'À l\'arrêt';

        driverMarkerRef.current = L.marker([lat, lng], { icon: driverIcon, zIndexOffset: 1000 })
          .bindPopup(`
            <div style="font-family:sans-serif; padding:4px;">
              <strong style="font-size:12px; color:#0f172a;">📍 Position actuelle du livreur</strong><br/>
              <span style="font-size:11px; color:#64748b;">Heure : <b>${timeStr}</b></span><br/>
              <span style="font-size:11px; color:#64748b;">Vitesse : <b>${speedStr}</b></span>
            </div>
          `)
          .addTo(map);
      }
    }

    // 6. Auto-fit bounds if we have points
    if (allPointsForBounds.length > 0) {
      try {
        const bounds = L.latLngBounds(allPointsForBounds);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      } catch (e) {}
    }

  }, [hubs, stops, plannedPolyline, routeColor, geohistory, selectedStopId]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}
